import { UserPermission } from '@ddv/models';

export type Permission = 'read' | 'write';

export interface Grant {
    name: string;
    permission: Permission;
}

export interface Sharing {
    toUsers: Grant[];
    toTeams: Grant[];
}

export interface LegacyGrant {
    name: string;
    privilege?: string;
    sharedType: 'USER' | 'TEAM';
}

export interface LegacySharing {
    isShared: boolean;
    dashboardPermissions?: LegacyGrant[];
}

// I'm assuming that the fact that the privilege is optional
// is just for the fact that the types in DDV are hideous.
// so we'll throw an error if it's missing, and if testing reveals that undefined is valid
// and actually means something, we can change it later.
function toGrantedPermission(privilege: string | undefined): Permission {
    if (!privilege) {
        throw new Error('Sharing privilege cannot be undefined');
    }

    if (privilege === 'VIEW') {
        return 'read';
    } else if (privilege === 'EDIT') {
        return 'write';
    } else {
        throw new Error(`Unknown sharing privilege: ${privilege}`);
    }
}

function toGrant(permission: UserPermission): Grant {
    return {
        name: permission.name,
        permission: toGrantedPermission(permission.privilege),
    };
}

export function emptySharing(): Sharing {
    return { toUsers: [], toTeams: [] };
}

export function fromLegacy(legacy: LegacySharing): Sharing {
    if (!legacy.dashboardPermissions) {
        return emptySharing();
    }

    return fromPermissions(legacy.dashboardPermissions);
}

export function fromPermissions(permissions: UserPermission[] | undefined): Sharing {
    if (!permissions) {
        return emptySharing();
    }

    return {
        toUsers: permissions
            .filter((p) => p.sharedType === 'USER')
            .map(toGrant),
        toTeams: permissions
            .filter((p) => p.sharedType === 'TEAM')
            .map(toGrant),
    };
}

export function toPermissions(sharing: Sharing): UserPermission[] {
    const permissions: UserPermission[] = [];

    sharing.toUsers.forEach((user) => {
        permissions.push({
            name: user.name,
            privilege: user.permission === 'read' ? 'VIEW' : 'EDIT',
            sharedType: 'USER',
        });
    });

    sharing.toTeams.forEach((team) => {
        permissions.push({
            name: team.name,
            privilege: team.permission === 'read' ? 'VIEW' : 'EDIT',
            sharedType: 'TEAM',
        });
    });

    return permissions;
}

export function isShared(sharing: Sharing | undefined): boolean {
    if (!sharing) {
        return false;
    }

    return sharing.toUsers.length > 0 || sharing.toTeams.length > 0;
}
